import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Styled from 'styled';
import { useQuery } from '@apollo/client/react/hooks';
import { toGlobalId } from 'graphql-relay';

import SVGIcon from 'lib/images/svg-pure-icon/svg-pure-icon';
import Image from 'lib/images/image-view';

import IconCamera from 'four-nets/icons/svg/icon_camera.svg';

import query, {
    RelatedInspirationsQuery,
    RelatedInspirationsQueryVariables,
    RelatedInspirationFragment,
} from './related-inspirations.graphql';
import defaultStyles from './related-inspirations.scss';

interface Props {
    styles: typeof defaultStyles;
    category: { id: number };
}

export interface RelatedInspirationProps {
    styles: typeof defaultStyles;
    inspiration: RelatedInspirationFragment;
}

const _RelatedInspiration: React.FC<React.PropsWithChildren<RelatedInspirationProps>> = props => {
    const { styles, inspiration } = props;

    const image1 = inspiration.tag.images?.edges[0] && inspiration.tag.images?.edges[0].node;
    const image2 = inspiration.tag.images?.edges[1] && inspiration.tag.images?.edges[1].node;
    const image3 = inspiration.tag.images?.edges[2] && inspiration.tag.images?.edges[2].node;

    return (
        <div className={styles.inspiration}>
            <Link to={`/inspirations/${inspiration.slug}/`}>
                <div className={styles.imageWrapper}>
                    <Image
                        sizes={{ xs: 150 }}
                        aspectFill={true}
                        src={image1?.photoFile?.src!}
                        srcSet={image1?.photoFile?.srcset!}
                        alt={inspiration.tag.name}
                        linkify={false}
                    />
                    <div className={styles.smallImages}>
                        <Image
                            sizes={{ xs: 150 }}
                            aspectFill={true}
                            src={image2?.photoFile?.src!}
                            srcSet={image2?.photoFile?.srcset!}
                            alt={image2?.alt}
                            linkify={false}
                        />
                        <Image
                            sizes={{ xs: 150 }}
                            aspectFill={true}
                            src={image3?.photoFile?.src!}
                            srcSet={image3?.photoFile?.srcset!}
                            alt={image3?.alt}
                            linkify={false}
                        />
                    </div>
                </div>
            </Link>
            <div className={styles.meta}>
                <h4 className={styles.inspirationTitle}>
                    <Link to={`/inspirations/${inspiration.slug}`}>{inspiration.tag.name}</Link>
                </h4>
                <SVGIcon height={15} width={18} src={IconCamera} />
                {inspiration.tag.imagesCount}
            </div>
        </div>
    );
};

export const RelatedInspiration = Styled(defaultStyles)(_RelatedInspiration);

const RelatedInspirations: React.FC<Props> = props => {
    const { styles, category } = props;
    const { data } = useQuery<RelatedInspirationsQuery, RelatedInspirationsQueryVariables>(query, {
        variables: { threadCategoryId: toGlobalId('ThreadCategoryNode', category.id.toString()) },
    });
    return data && data.threadTagSlugs?.edges.length ? (
        <div>
            <h3 className={styles.heading}>
                <FormattedMessage id='RECOMMENDED_INSPIRATIONS' />
            </h3>
            <div className={styles.inspirations}>
                {data?.threadTagSlugs?.edges.map(edge => (
                    <RelatedInspiration key={edge?.node?.id} inspiration={edge?.node!} />
                ))}
            </div>
        </div>
    ) : null;
};

export default Styled(defaultStyles)(RelatedInspirations);
